import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  FaCog,
  FaUsers,
  FaPuzzlePiece,
  FaShieldAlt,
  FaPlug,
  FaBan,
  FaMicrosoft,
  FaInbox,
  FaRss,
  FaProjectDiagram,
  FaFingerprint,
  FaFlask,
  FaSearch,
  FaTimes,
  FaRobot,
  FaLock,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { canManageUsers, canManageLists, canAccessSettings, isPlatformAdmin } from "../constants/roles";
import UserManagement from "../components/UserManagement/UserManagement";
import ModuleSettings from "../components/Admin/ModuleSettings";
import WhitelistedIocs from "../components/Admin/WhitelistedIocs";
import BlacklistedIocs from "../components/Admin/BlacklistedIocs";
import ApiKeysSettings from "../components/Admin/ApiKeysSettings";
import AdvancedAnalysisSettings from "../components/Admin/AdvancedAnalysisSettings";
import ThreatIntelSourcesSettings from "../components/Admin/ThreatIntelSourcesSettings";
import SsoSettings from "../components/Admin/SsoSettings";
import DefenderIntegrationSettings from "../components/Admin/DefenderIntegrationSettings";
import DetectionRules from "../components/Admin/DetectionRules";
import ProtectedDomains from "../components/Admin/ProtectedDomains";
import MfaSettings from "../components/Admin/MfaSettings";
import "./AdminSettingsPage.css";

// Capability keys used to gate each item. Resolved against the current user's
// role below — keeps the group config declarative.
const CAP = { USER: "user", PLATFORM: "platform", LIST: "list" };

// Stable category scopes so ApiKeysSettings can power two pages without
// re-filtering its CATEGORIES on every render.
const AI_CATEGORIES = ["ai"];
const KEY_CATEGORIES = ["osint", "sandbox", "platform"];

// Declarative nav model: grouped sections, each item carries the metadata the
// sidebar (label, icon, description) and search (label + description + keywords)
// need, plus the component it renders.
const GROUPS = [
  {
    id: "people",
    label: "People & Access",
    items: [
      {
        id: "users",
        label: "User Management",
        desc: "Invite analysts, assign roles, and deactivate accounts.",
        keywords: "user role invite analyst account permission deactivate",
        icon: <FaUsers />,
        cap: CAP.USER,
        Component: () => <UserManagement embedded />,
      },
      {
        id: "sso",
        label: "Single Sign-On",
        desc: "Azure Entra ID / OIDC login configuration.",
        keywords: "sso oidc azure entra microsoft login saml federation",
        icon: <FaMicrosoft />,
        cap: CAP.PLATFORM,
        Component: SsoSettings,
      },
      {
        id: "mfa",
        label: "Multi-Factor Authentication",
        desc: "Require email or SMS one-time codes for all non-SSO logins.",
        keywords: "mfa 2fa two factor otp one-time code email sms twilio smtp verification",
        icon: <FaLock />,
        cap: CAP.PLATFORM,
        Component: MfaSettings,
      },
    ],
  },
  {
    id: "detection",
    label: "Detection",
    items: [
      {
        id: "modules",
        label: "Modules",
        desc: "Enable or disable analysis tools across the platform.",
        keywords: "module feature toggle enable disable tool",
        icon: <FaPuzzlePiece />,
        cap: CAP.PLATFORM,
        Component: ModuleSettings,
      },
      {
        id: "detection-rules",
        label: "Detection Rules",
        desc: "Custom condition rules that raise or suppress verdicts.",
        keywords: "rule detection use case verdict raise suppress custom heuristic",
        icon: <FaProjectDiagram />,
        cap: CAP.PLATFORM,
        Component: DetectionRules,
      },
      {
        id: "protected-domains",
        label: "Protected Domains",
        desc: "Domains watched for lookalike / homoglyph spoofing.",
        keywords: "domain homoglyph lookalike spoof protected brand impersonation",
        icon: <FaFingerprint />,
        cap: CAP.PLATFORM,
        Component: ProtectedDomains,
      },
      {
        id: "advanced-analysis",
        label: "Advanced Analysis",
        desc: "Sandbox detonation behavior, including when it is offered.",
        keywords: "advanced analysis sandbox detonation hybrid anyrun malicious verdict hide suppress detonate",
        icon: <FaFlask />,
        cap: CAP.PLATFORM,
        Component: AdvancedAnalysisSettings,
      },
    ],
  },
  {
    id: "ai",
    label: "AI",
    items: [
      {
        id: "ai-configurations",
        label: "AI Configurations",
        desc: "Enable AI platform-wide and choose the provider, model, and API key.",
        keywords: "ai configuration provider model openai anthropic google gemini enable disable toggle master switch llm",
        icon: <FaRobot />,
        cap: CAP.PLATFORM,
        Component: () => <ApiKeysSettings categories={AI_CATEGORIES} />,
      },
    ],
  },
  {
    id: "lists",
    label: "IoC Lists",
    items: [
      {
        id: "whitelist",
        label: "IoC Whitelist",
        desc: "Trusted indicators that are always treated as benign.",
        keywords: "whitelist allowlist ioc trusted benign indicator safe",
        icon: <FaShieldAlt />,
        cap: CAP.LIST,
        Component: WhitelistedIocs,
      },
      {
        id: "blacklist",
        label: "IoC Blacklist",
        desc: "Known-bad indicators that always flag as malicious.",
        keywords: "blacklist blocklist ioc malicious bad indicator deny",
        icon: <FaBan />,
        cap: CAP.LIST,
        Component: BlacklistedIocs,
      },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    items: [
      {
        id: "integration",
        label: "API Keys",
        desc: "OSINT, sandbox, and platform integration credentials.",
        keywords: "api key integration virustotal abuseipdb urlscan otx sandbox hybrid anyrun osint provider credential outlook",
        icon: <FaPlug />,
        cap: CAP.PLATFORM,
        Component: () => <ApiKeysSettings categories={KEY_CATEGORIES} />,
      },
      {
        id: "threat-intel",
        label: "Threat Intel Sources",
        desc: "RSS feeds powering the Threat Intelligence module.",
        keywords: "threat intel rss feed source news intelligence",
        icon: <FaRss />,
        cap: CAP.PLATFORM,
        Component: ThreatIntelSourcesSettings,
      },
      {
        id: "defender",
        label: "User Submissions",
        desc: "Ingest user-reported emails from the Defender mailbox.",
        keywords: "defender submission reported email mailbox graph ingest user",
        icon: <FaInbox />,
        cap: CAP.PLATFORM,
        Component: DefenderIntegrationSettings,
      },
    ],
  },
];

export default function AdminSettingsPage() {
  const { user } = useAuth();

  const caps = useMemo(
    () => ({
      [CAP.USER]: canManageUsers(user?.role),
      [CAP.PLATFORM]: isPlatformAdmin(user?.role),
      [CAP.LIST]: canManageLists(user?.role),
    }),
    [user?.role]
  );

  // Groups the current user is allowed to see, with items they lack the
  // capability for filtered out and empty groups dropped.
  const allowedGroups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((it) => caps[it.cap]),
      })).filter((g) => g.items.length > 0),
    [caps]
  );

  const allItems = useMemo(() => allowedGroups.flatMap((g) => g.items), [allowedGroups]);

  const [query, setQuery] = useState("");
  const [active, setActive] = useState(() => null);

  // Search filters items by label / description / keywords.
  const q = query.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    if (!q) return allowedGroups;
    return allowedGroups
      .map((g) => ({
        ...g,
        items: g.items.filter((it) =>
          `${it.label} ${it.desc} ${it.keywords}`.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [allowedGroups, q]);

  if (!canAccessSettings(user?.role)) return <Navigate to="/" replace />;

  // Resolve the active item: explicit selection if still valid, else the first
  // available item. Selection survives searching/clearing the filter.
  const activeId = allItems.some((it) => it.id === active) ? active : allItems[0]?.id;
  const activeItem = allItems.find((it) => it.id === activeId);
  const ActiveComponent = activeItem?.Component;

  return (
    <div className="admin-settings-page">
      <div className="admin-settings-header">
        <div className="admin-settings-icon">
          <FaCog />
        </div>
        <div>
          <h1>Settings</h1>
          <p className="admin-settings-sub">Administrative configuration for PhishMatrix AI</p>
        </div>
      </div>

      <div className="admin-settings-layout">
        <aside className="admin-settings-nav" aria-label="Settings sections">
          <div className="admin-settings-search">
            <FaSearch className="admin-settings-search-icon" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search settings…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search settings"
            />
            {query && (
              <button
                type="button"
                className="admin-settings-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>

          <div className="admin-settings-nav-scroll">
            {visibleGroups.length === 0 ? (
              <p className="admin-settings-noresults">No settings match “{query}”.</p>
            ) : (
              visibleGroups.map((g) => (
                <div key={g.id} className="admin-settings-group">
                  <div className="admin-settings-group-label">{g.label}</div>
                  {g.items.map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      className={`admin-settings-navitem ${activeId === it.id ? "active" : ""}`}
                      aria-current={activeId === it.id ? "page" : undefined}
                      onClick={() => setActive(it.id)}
                      title={it.desc}
                    >
                      <span className="admin-settings-navitem-rail" aria-hidden="true" />
                      <span className="admin-settings-navitem-icon">{it.icon}</span>
                      <span className="admin-settings-navitem-label">{it.label}</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </aside>

        <section className="admin-settings-content" aria-label={activeItem?.label}>
          {ActiveComponent && <ActiveComponent />}
        </section>
      </div>
    </div>
  );
}
